import { useState, useRef } from 'react';
import { useFeedStore } from '@/stores/feed';
import { useAuthStore } from '@/stores/auth';
import { mediaUrl } from '@/hooks/useMediaUrl';
import { api } from '@/lib/api';

interface PostComposerProps {
  wallOwnerId?: string;
  wallOwnerName?: string;
  onPostCreated?: () => void;
  defaultContent?: string;
}

export function PostComposer({ wallOwnerId, wallOwnerName, onPostCreated, defaultContent }: PostComposerProps) {
  const { user } = useAuthStore();
  const { createPost } = useFeedStore();
  const [content, setContent] = useState(defaultContent || '');
  const [posting, setPosting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const placeholder = wallOwnerName
    ? `Drop a note on ${wallOwnerName}'s wall...`
    : `What's your vibe, ${user?.display_name?.split(' ')[0] || ''}?`;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('File too large (10MB max)');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      alert('Only JPEG, PNG, WebP, or GIF allowed');
      return;
    }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePost = async () => {
    if (!content.trim() && !imageFile) return;
    setPosting(true);

    try {
      let imageKey: string | undefined;

      // Upload image first if present
      if (imageFile) {
        setUploading(true);
        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('purpose', 'posts');
        const result = await api.upload<{ key: string }>('/media/upload', formData);
        imageKey = result.key;
        setUploading(false);
      }

      await createPost(content.trim(), {
        wall_owner_id: wallOwnerId,
        image_key: imageKey,
      });

      setContent('');
      clearImage();
      onPostCreated?.();
    } catch {
      // ignore
    } finally {
      setPosting(false);
      setUploading(false);
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-header flex items-center gap-1.5">
        <span className="text-amber-400">✦</span>
        {wallOwnerName ? `${wallOwnerName}'s Wall` : "What's your vibe?"}
      </div>
      <div className="p-3">
        <div className="flex gap-2">
          <img src={mediaUrl(user?.avatar_key)} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
            placeholder={placeholder}
            rows={2}
            className="input w-full resize-none text-sm bg-sand-50"
          />
        </div>

        {/* Image preview */}
        {imagePreview && (
          <div className="mt-2 relative inline-block">
            <img src={imagePreview} alt="Upload preview" className="max-h-40 rounded border border-sand-300" />
            <button
              onClick={clearImage}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
            >
              ✕
            </button>
          </div>
        )}

        <div className="flex justify-between items-center mt-2">
          <div className="flex gap-3 text-sand-500 text-xs">
            <label className="cursor-pointer hover:text-sand-700 flex items-center gap-1">
              📷 Photo
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
            <span className="cursor-pointer hover:text-sand-700">🔗 Link</span>
          </div>
          <button
            onClick={handlePost}
            disabled={posting || uploading || (!content.trim() && !imageFile)}
            className="btn-primary text-xs py-1 px-4 disabled:opacity-40"
          >
            {uploading ? 'Uploading...' : posting ? 'Posting...' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
}
